// When a new quarter rolls, NOW_INTERVAL_ID and CREATION_IDS will automatically update
// based on system date. Enjoy!!!

import getDateFromString from '@/utils/getDateFromString'
import getToday from '@/utils/getToday'

function quarterlyIntervalIdFromComponents({ quarter, year }) {
  return `q${quarter}-${year}`
}

function quarterlyIntervalIdToComponents(id) {
  const match = id.match(/^q([1-4])-([0-9]{4})$/)
  return { quarter: parseInt(match[1]), year: parseInt(match[2]) }
}

function yearForQuarterlyIntervalId(id) {
  return quarterlyIntervalIdToComponents(id).year
}

function intervalIdForYear(year) {
  return year.toString()
}

function quarterlyIntervalIdForDate(date = null) {
  if (!date) {
    date = new Date()
  }
  const quarter = Math.floor(date.getMonth() / 3) + 1
  const plainIntervalId = quarterlyIntervalIdFromComponents({
    quarter: quarter,
    year: date.getFullYear()
  })
  return `${plainIntervalId}`
}

function previousQuarterlyIntervalId(nowId) {
  const { quarter, year } = quarterlyIntervalIdToComponents(nowId)
  if (quarter === 1) {
    return quarterlyIntervalIdFromComponents({ quarter: 4, year: year - 1 })
  } else {
    return quarterlyIntervalIdFromComponents({ quarter: quarter - 1, year: year })
  }
}

function nextQuarterlyIntervalId(nowId) {
  const { quarter, year } = quarterlyIntervalIdToComponents(nowId)
  if (quarter === 4) {
    return quarterlyIntervalIdFromComponents({ quarter: 1, year: year + 1 })
  } else {
    return quarterlyIntervalIdFromComponents({ quarter: quarter + 1, year: year })
  }
}

export function creationIds(date = null, nextCount = 3) {
  const nowId = quarterlyIntervalIdForDate(date)
  const nowYear = yearForQuarterlyIntervalId(nowId)
  const previousId = previousQuarterlyIntervalId(nowId)
  const previousYear = yearForQuarterlyIntervalId(previousId)
  const nextIds = []
  for (let i = 0, currentId = nowId; i < nextCount; i++) {
    currentId = nextQuarterlyIntervalId(currentId)
    nextIds.push(currentId)
  }
  const ids = []
  if (previousYear !== nowYear) {
    ids.push(intervalIdForYear(previousYear))
    ids.push(previousId)
    ids.push(intervalIdForYear(nowYear))
  } else {
    ids.push(intervalIdForYear(nowYear))
    ids.push(previousId)
  }
  ids.push(nowId)
  let onNextYear = false
  nextIds.forEach(nextId => {
    const nextYear = yearForQuarterlyIntervalId(nextId)
    if (nextYear !== nowYear && !onNextYear) {
      ids.push(intervalIdForYear(nextYear))
      onNextYear = true
    }
    ids.push(nextId)
  })
  return ids
}

export const ACTIVE_QUARTER_ID = quarterlyIntervalIdForDate()
const ACTIVE_YEAR_ID = ACTIVE_QUARTER_ID.slice(3)
const NOW_YEAR = ACTIVE_YEAR_ID
const NOW_QUARTER = ACTIVE_QUARTER_ID.slice(0, 2)
export const IntervalId = {
  ANNUAL: ACTIVE_YEAR_ID,
  PAST: 'past',
  FUTURE: 'future'
}
const FILTER_IDS = (window.NORTH_FILTER_IDS = [
  ACTIVE_YEAR_ID,
  ACTIVE_QUARTER_ID,
  `${ACTIVE_YEAR_ID}-exact`,
  `${ACTIVE_QUARTER_ID}-exact`,
  IntervalId.PAST,
  IntervalId.FUTURE
])
const CREATION_IDS = creationIds()

export const DurationType = {
  QUARTER: 'quarter',
  INFINITE: 'infinite',
  YEAR: 'year'
}

export function intervalWithId(id, { matchExact = false } = {}) {
  if (id.match(/^[0-9]{4}(-only|-exact)?$/)) {
    const year = id.slice(0, 4)
    const exactSubset = id.endsWith('-only') || id.endsWith('-exact')
    if (matchExact || exactSubset) {
      return {
        id: id,
        name: `Annual ${year}`,
        shortName: year === NOW_YEAR ? year : null,
        description: `Annual ${year} only`,
        duration: DurationType.YEAR,
        start: year + '-01-01',
        end: year + '-12-31',
        subset: 'exact',
        filterIcon: 'timeline-pie'
      }
    } else {
      return {
        id: id,
        name: year === NOW_YEAR ? `All ${year} goals` : `Annual ${year}`,
        shortName: year === NOW_YEAR ? `All ${year}` : null,
        description: `Q1, Q2, Q3, Q4 + Annual ${year}`,
        duration: DurationType.YEAR,
        start: year + '-01-01',
        end: year + '-12-31'
      }
    }
  } else if (id.match(/^q[1-4]-[0-9]{4}(-only|-exact)?$/)) {
    const year = id.slice(3, 7)
    const quarter = id.slice(0, 2)
    const qDisplay = quarter.toUpperCase()
    const qStarts = {
      q1: '01-01',
      q2: '04-01',
      q3: '07-01',
      q4: '10-01'
    }
    const qEnds = {
      q1: '03-31',
      q2: '06-30',
      q3: '09-30',
      q4: '12-31'
    }
    const exactSubset = id.endsWith('-only') || id.endsWith('-exact')
    const isActiveQuarter = `${quarter}-${year}` === ACTIVE_QUARTER_ID
    if (matchExact || exactSubset) {
      return {
        id: id,
        name: `${qDisplay} ${year}`,
        shortName: year === NOW_YEAR ? qDisplay : null,
        description: `${qDisplay} ${year} only`,
        duration: DurationType.QUARTER,
        start: year + '-' + qStarts[quarter],
        end: year + '-' + qEnds[quarter],
        subset: 'exact',
        filterIcon: 'timeline-pie'
      }
    } else {
      return {
        id: id,
        name: isActiveQuarter ? `Active ${year}` : `${qDisplay} ${year}`,
        shortName: isActiveQuarter ? `Active ${year}` : qDisplay,
        description: `${qDisplay} + Annual ${year}`,
        duration: DurationType.QUARTER,
        start: year + '-' + qStarts[quarter],
        end: year + '-' + qEnds[quarter],
        filterIcon: 'active-timeline-icon'
      }
    }
  } else if (id === 'any') {
    return {
      id: id,
      name: 'All',
      genericName: 'All Timelines',
      description: 'Anything from past, present and future',
      duration: DurationType.INFINITE
    }
  } else if (id === 'past') {
    const previousYear = NOW_QUARTER === 'q1' ? (parseInt(NOW_YEAR) - 1).toString() : NOW_YEAR
    const previousQuarter = {
      q1: 'q4',
      q2: 'q1',
      q3: 'q2',
      q4: 'q3'
    }[NOW_QUARTER]
    const previousIntervalId = previousQuarter + '-' + previousYear
    return {
      id: id,
      name: 'Past goals',
      genericName: 'Past',
      description: `Everything before ${NOW_QUARTER.toUpperCase()} ${NOW_YEAR}`,
      duration: DurationType.INFINITE,
      end: intervalWithId(previousIntervalId).end
    }
  } else if (id === 'future') {
    const nextYear = NOW_QUARTER === 'q4' ? (parseInt(NOW_YEAR) + 1).toString() : NOW_YEAR
    const nextQuarter = {
      q1: 'q2',
      q2: 'q3',
      q3: 'q4',
      q4: 'q1'
    }[NOW_QUARTER]
    const nextIntervalId = nextQuarter + '-' + nextYear
    return {
      id: id,
      name: 'Upcoming goals',
      genericName: 'Upcoming',
      description: `Everything beyond ${NOW_QUARTER.toUpperCase()} ${NOW_YEAR}`,
      duration: DurationType.INFINITE,
      start: intervalWithId(nextIntervalId).start
    }
  } else {
    throw new Error('Unknown interval id: ' + id)
  }
}

window.intervalWithId = intervalWithId

export function filterIntervals() {
  return FILTER_IDS.map(intervalWithId)
}

export function creationIntervals() {
  return CREATION_IDS.map(id => intervalWithId(id, { matchExact: true }))
}

function relativeIntervalId(id) {
  const nowInterval = intervalWithId(ACTIVE_QUARTER_ID)
  const givenInterval = intervalWithId(id)
  if (id === ACTIVE_QUARTER_ID) {
    return id
  } else if (givenInterval.end < nowInterval.start) {
    return 'past'
  } else if (givenInterval.start > nowInterval.end) {
    return 'future'
  } else if (
    givenInterval.duration === DurationType.YEAR &&
    givenInterval.start <= nowInterval.start &&
    givenInterval.end >= nowInterval.end
  ) {
    return id
  } else {
    throw new Error('Unknown interval id: ' + id)
  }
}

export function filterIdForCreationId(id) {
  return relativeIntervalId(id)
}

const DEFAULT_ID_KEY = 'intervalDefaultId'

export function getDefaultIntervalId() {
  return localStorage.getItem(DEFAULT_ID_KEY) || ACTIVE_QUARTER_ID
}

export function setDefaultIntervalId(id) {
  if (intervalWithId(id)) {
    localStorage.setItem(DEFAULT_ID_KEY, id)
  } else {
    console.warn('No interval with id', id)
  }
}

export function defaultInterval() {
  return intervalWithId(getDefaultIntervalId())
}

export function nowInterval() {
  return intervalWithId(ACTIVE_QUARTER_ID)
}

export function currentAndFutureTimelines() {
  return CREATION_IDS.filter(id => relativeIntervalId(id) !== 'past')
}

export const isOlderTimeline = timeline => {
  if (!timeline) return false
  const timelineDetails = intervalWithId(timeline)
  const end = timelineDetails?.end
  if (end) {
    const endDate = new Date(end)
    const today = getToday()
    return today.getTime() > endDate.getTime()
  }
}

export function getTimelineCompletedPercent(timeline) {
  const start = getDateFromString(timeline.start)
  const today = getToday()
  const timeElapsed = today.getTime() - start.getTime()
  const totalTime = getTimelineDuration(timeline)
  return (timeElapsed * 100) / totalTime
}

export function getTimelineDuration(timeline) {
  const start = getDateFromString(timeline.start)
  const end = getDateFromString(timeline.end)
  return end.getTime() - start.getTime()
}

export function isActiveInterval(interval) {
  const today = getToday()
  const start = getDateFromString(interval.start)
  const end = getDateFromString(interval.end)
  return start.getTime() <= today.getTime() && end.getTime() >= today.getTime()
}

export function getDaysLeftInTimeline(timeline) {
  const today = getToday()
  const start = getDateFromString(timeline.start)
  const totalTime = getTimelineDuration(timeline)
  const timeElapsed = today.getTime() - start.getTime()
  const timeLeft = totalTime - timeElapsed
  return Math.ceil(timeLeft / (1000 * 60 * 60 * 24))
}
